import React, { FC, useState } from 'react';

import { CalendarTodayOutlined } from '@material-ui/icons';
import DownloadIconRaw from '@material-ui/icons/GetApp';
import { DateTime } from 'luxon';
import { useApolloClient, useMutation } from 'react-apollo';
import styled from 'styled-components';

import { useDates } from 'Hooks';

import { GeneratePDFReport, GeneratePDFReportVariables } from './Components/__queries__';
import CalendarModal from './Components/CalendarModal';
import MonthRow, { GENERATE_REPORT, REPORTS_QUERY } from './Components/MonthRow';
import ReportDetails from './Components/ReportDetails';
import Spinner from './Components/Spinner';
import LegalEntitySelector from '../Transactions/Components/LegalEntitySelector';

const Container = styled.div`
  color: #333;
  padding: 16px;
`;

const Section = styled.div`
  max-width: 600px;
  padding-bottom: 16px;
`;

const FlexContainer = styled.div`
  display: flex;
  flex-direction: column;

  @media (min-width: 1280px) {
    flex-direction: row;
    gap: 24px;
  }
`;

const LeftPanel = styled.div`
  width: 100%;

  @media (min-width: 1280px) {
    width: 33%;
  }
`;

const RightPanel = styled.div`
  width: 100%;
  margin-top: 24px;

  @media (min-width: 1280px) {
    width: 67%;
    margin-top: 0;
  }
`;

const Heading = styled.h1`
  text-align: center;
  font-size: 1.875rem;
  font-weight: bold;
  padding: 16px;
`;

const CalendarDetails = styled.span`
  font-size: 1rem;
  font-weight: 600;
`;

const HeadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  width: 100%;
`;

const CalendarIcon = styled(CalendarTodayOutlined)`
  cursor: pointer;
  color: #333;
  font-size: 1.5rem;
  position: absolute;
  right: 0;
`;

const SubHeading = styled.h2`
  text-align: center;
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 16px;
`;

const Card = styled.div`
  border: 1px solid #ddd;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  padding: 24px;
  background-color: #fff;
`;

const YearSection = styled.div`
  border: 1px solid #ddd;
  border-radius: 8px;
  margin-bottom: 16px;
`;

const YearHeader = styled.h3`
  padding: 16px;
  background-color: #f0f0f0;
  font-size: 1.125rem;
  font-weight: 600;
`;

const Placeholder = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 160px;
  font-size: 1.125rem;
`;

const DownloadIcon = styled(DownloadIconRaw)`
  height: 24px;
  width: 24px;
  margin-left: 8px;
  cursor: pointer;
  color: #14b8a6;
`;

const AdyenReports: FC = () => {
  const [legalEntityId, setLegalEntityId] = useState<string | null>(null);
  const [balanceAccountId, setBalanceAccountId] = useState<string | null>(null);
  const dates = useDates();
  const [selectedReport, setSelectedReport] = useState<any | null>(null);
  const [isCalendarOpen, setCalendarOpen] = useState(false);
  const [loadingPDFReport, setLoadingPDFReport] = useState(false);
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [reportDetailsType, setReportDetailsType] = useState<'CALENDAR' | 'MONTH'>('MONTH');

  const client = useApolloClient();
  const [generatePdfReport] = useMutation<GeneratePDFReport, GeneratePDFReportVariables>(
    GENERATE_REPORT,
  );

  const allMonths = [
    { name: 'January', index: 0 },
    { name: 'February', index: 1 },
    { name: 'March', index: 2 },
    { name: 'April', index: 3 },
    { name: 'May', index: 4 },
    { name: 'June', index: 5 },
    { name: 'July', index: 6 },
    { name: 'August', index: 7 },
    { name: 'September', index: 8 },
    { name: 'October', index: 9 },
    { name: 'November', index: 10 },
    { name: 'December', index: 11 },
  ];

  const handleCalendarSubmit = async (calendarStartDate, calendarEndDate) => {
    setReportDetailsType('CALENDAR');

    // Formats the date to ISO string start of day, i.e if you select 7th of April then this would be: 2025-04-07T00:00:00.000Z
    const formattedStartDate = dates
      .getUTCStartOfDay(
        calendarStartDate.getFullYear(),
        calendarStartDate.getMonth() + 1,
        calendarStartDate.getDate(),
      )
      .toISO();

    // Formats the date to ISO string end of day, i.e if you select 9th of April then this would be: 2025-04-09T23:59:59.999Z
    const formattedEndDate = dates
      .getUTCEndOfDay(
        calendarEndDate.getFullYear(),
        calendarEndDate.getMonth() + 1,
        calendarEndDate.getDate(),
      )
      .toISO();

    const query = await client.query({
      query: REPORTS_QUERY,
      variables: {
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        legalEntityId: legalEntityId,
      },
    });

    setStartDate(formattedStartDate);
    setEndDate(formattedEndDate);
    setSelectedReport(query.data?.adyenBalanceLegalEntity?.balanceAccounts?.[0]?.report);
    setCalendarOpen(false);
  };

  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth();

  const getMonthsForYear = (year: number) => {
    if (year < currentYear) return [...allMonths].reverse();
    if (year === currentYear) return [...allMonths.slice(0, currentMonth + 1)].reverse();
    return [];
  };

  const getStartEndDate = (month: any, year: number) => {
    // Formats the date to ISO string start of day, i.e if you select February then this would be: 2025-02-01T00:00:00.000Z
    const firstDay = dates.getUTCStartOfDay(year, month.index + 1, 1).toISO();

    // Get current month so we know the last day of the month
    const currentMonth = DateTime.utc().set({ month: month.index + 1, year });
    // Formats the date to ISO string end of day, i.e if you select February then this would be: 2025-02-28T23:59:59.999Z
    const lastDay = dates.getUTCEndOfDay(year, month.index + 1, currentMonth.daysInMonth).toISO();

    return { startDate: firstDay!, endDate: lastDay! };
  };

  const handleMonthClick = (reportData: any) => {
    setReportDetailsType('MONTH');
    setSelectedReport(reportData);
  };

  const handleReportDownload = async () => {
    setLoadingPDFReport(true);

    try {
      const result = await generatePdfReport({
        variables: {
          data: {
            startDate: startDate!,
            endDate: endDate!,
            balanceAccountId: balanceAccountId!,
          },
        },
      });
      setLoadingPDFReport(false);

      const success = result.data?.generateAdyenPDFReport.success;
      const message = result.data?.generateAdyenPDFReport.message;

      if (!success) throw new Error('Failed to generate PDF report');

      // TODO: FIX
      const date = DateTime.fromISO(startDate!).toFormat('yyyy_MM');
      const a = document.createElement('a');
      a.href = `data:application/pdf;base64,${message}`;
      a.download = `Baemingo_Monthly_Fee_Statement_${date}.pdf`;
      a.click();
    } catch (error) {
      setLoadingPDFReport(false);
    }
  };

  return (
    <Container>
      <Section>
        <LegalEntitySelector
          onChange={setLegalEntityId}
          onBalancecAccountIdChange={setBalanceAccountId}
        />
      </Section>
      <FlexContainer>
        <LeftPanel>
          {/* <Heading>Specific-Dates Reports</Heading>
          <Card>
            <CalendarWrapper>
              <Calendar
                range
                selectedDates={[calendarStartDate, calendarEndDate]}
                minDate={new Date('2022-06-01')}
                maxDate={new Date()}
                setSelectedDates={(selectedDates) => {
                  selectedDates[0] && setCalendarStartDate(selectedDates[0]);
                  selectedDates[1] && setCalendarEndDate(selectedDates[1]);
                }}
              />
            </CalendarWrapper>
          </Card> */}
          <HeadingContainer>
            <Heading>Monthly Reports</Heading>
            <CalendarIcon onClick={() => setCalendarOpen(true)} />
          </HeadingContainer>
          <Card>
            <SubHeading>Summary of payouts and fees</SubHeading>
            <YearSection>
              {currentYear > 2024 && (
                <YearSection>
                  <YearHeader>{currentYear}</YearHeader>
                  {getMonthsForYear(currentYear).map((month) => {
                    const { startDate, endDate } = getStartEndDate(month, currentYear);
                    return (
                      <MonthRow
                        key={month.name}
                        month={month}
                        year={currentYear}
                        startDate={startDate}
                        endDate={endDate}
                        onClick={handleMonthClick}
                        balanceAccountId={balanceAccountId!}
                        legalEntityId={legalEntityId!}
                      />
                    );
                  })}
                </YearSection>
              )}

              <YearHeader>2024</YearHeader>
              {getMonthsForYear(2024).map((month, index) => {
                const { startDate, endDate } = getStartEndDate(month, 2024);
                return (
                  <MonthRow
                    key={month.name}
                    isFirst={index === 0}
                    month={month}
                    year={2024}
                    startDate={startDate}
                    endDate={endDate}
                    onClick={handleMonthClick}
                    balanceAccountId={balanceAccountId!}
                    legalEntityId={legalEntityId!}
                  />
                );
              })}
            </YearSection>
          </Card>
        </LeftPanel>

        <RightPanel>
          <Heading>
            Report Details
            {selectedReport &&
              selectedReport?.length > 0 &&
              reportDetailsType === 'CALENDAR' &&
              startDate !== null &&
              endDate !== null &&
              (loadingPDFReport ? (
                <Spinner />
              ) : (
                <>
                  <DownloadIcon
                    onClick={(e) => {
                      e.stopPropagation();
                      handleReportDownload();
                    }}
                  />
                  <br />
                  <CalendarDetails>
                    {DateTime.fromISO(startDate).toUTC().toFormat('yyyy-MM-dd')} -{' '}
                    {DateTime.fromISO(endDate).toUTC().toFormat('yyyy-MM-dd')}
                  </CalendarDetails>
                </>
              ))}
          </Heading>
          {selectedReport && selectedReport?.length > 0 ? (
            <ReportDetails reportData={selectedReport} />
          ) : (
            <Card>
              <Placeholder>Select a month to view details</Placeholder>
            </Card>
          )}
        </RightPanel>
      </FlexContainer>

      <CalendarModal
        isOpen={isCalendarOpen}
        onClose={() => setCalendarOpen(false)}
        onSubmit={handleCalendarSubmit}
      />
    </Container>
  );
};

export default AdyenReports;
